import { cn } from '../../../lib/utils';
import GenericCardMark from './marks/generic-card.svg?react';
import MastercardMark from './marks/mastercard.svg?react';
import PaypalMark from './marks/paypal.svg?react';
import SepaMark from './marks/sepa.svg?react';
import VisaMark from './marks/visa.svg?react';

// `width` is the mark's share of the badge, measured off the design system's "Payment Method / *"
// components. Height follows from the viewBox — the Figma exports ship preserveAspectRatio="none",
// which the render below overrides so a wrong width can mis-size a mark but never squash it.
const BRANDS = {
  paypal: { Mark: PaypalMark, label: 'PayPal', width: 'w-[57.1%]' },
  visa: { Mark: VisaMark, label: 'Visa', width: 'w-[67.7%]' },
  mastercard: { Mark: MastercardMark, label: 'Mastercard', width: 'w-[64.3%]' },
  sepa: { Mark: SepaMark, label: 'SEPA', width: 'w-[71.4%]' },
  // No wordmark to name it — callers that show it as meaningful pass `label`.
  'generic-card': { Mark: GenericCardMark, label: undefined, width: 'w-[54.2%]' },
} as const;

export type PaymentBrand = keyof typeof BRANDS;

interface PaymentMethodIconProps {
  brand: PaymentBrand;
  /** Accessible name, overriding the brand's own. Required to name `generic-card`, which has none. */
  label?: string;
  /** Drops the badge outline for surfaces that frame it themselves, e.g. the redirect spinner ring. */
  borderless?: boolean;
  /** Sizing lives here: the badge is square, so `size-6` (default) / `size-8` / `size-12`. */
  className?: string;
}

/**
 * A payment method's brand mark on the white badge from the design system's "Payment Method / *"
 * components. Unlabelled badges render as decorative, for surfaces that name the method in text.
 */
export function PaymentMethodIcon({ brand, label, borderless, className }: PaymentMethodIconProps) {
  const { Mark, label: brandLabel, width } = BRANDS[brand];
  const name = label ?? brandLabel;

  return (
    <span
      {...(name ? ({ role: 'img', 'aria-label': name } as const) : ({ 'aria-hidden': true } as const))}
      className={cn(
        // Radius as a percentage: the design specifies 4px at 24 but 6.4px at 48, and we also render at
        // 32 and 80. White fill and near-black outline are literal — the badge stays white in dark mode,
        // where `border-border-subtle` would resolve to an invisible white.
        'inline-flex size-6 items-center justify-center rounded-[16.7%] bg-white',
        // An inset ring, not a border: a border would eat 2px of the content box the mark's percentage
        // width resolves against, so marks would shrink below their Figma size and change size with
        // `borderless`. A ring is a box-shadow — no layout effect either way.
        !borderless && 'ring-1 ring-black/10 ring-inset',
        className,
      )}
    >
      <Mark className={cn(width, 'h-auto')} preserveAspectRatio="xMidYMid meet" aria-hidden />
    </span>
  );
}
